import StatCards from './ui/StatCards';
import UserAvatar from "./UserAvatar";
import fetch from '../utils/authenticatedFetch';
import ModalLayer from './ModalLayer';
import React, {
  useState,
  useEffect,
  useMemo,
  ChangeEvent,
  FormEvent,
  useCallback,
  useRef,
} from 'react';
import './UserManagement.css';
import {
  FaChevronDown,
  FaChevronRight,
  FaFilter,
  FaSyncAlt,
  FaCopy,
  FaKey,
  FaUserPlus,
  FaTimes,
  FaEye,
} from 'react-icons/fa';

/* =========================
   Types
========================= */
interface User {
  id: number;
  name: string;
  surname: string;
  title: string;
  specialization: string;
  gender: string;
  nationalId: string;
  dateOfBirth: string;
  nationality: string;
  highestLevelOfEducation: string;
  contactNumber: string;
  email: string;
  password: string;
  userType: string;
  academicQualifications: string | null;
  registrationNo: string;
  faculty: any;
  department: string | null;
  status: string | null;
  departmentId?: number;
}

interface Faculty {
  id: number;
  code: string | null;
  name: string;
  description: string;
}

interface Department {
  id: number;
  departmentCode: string;
  name: string;
  description: string;
  facultyId: number;
}

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  titleId?: string;
}

/* =========================
   Small utilities
========================= */
const API = '';

const USER_TYPE_LABELS: Record<string, string> = {
  ADMIN_CYBER: 'System Admin',
  RESEARCH_CHAIR: 'Research Ethics Committee Chair',
  PROFESSOR: 'Professor',
  RESEARCH_OFFICER: 'Research Officer',
  FACULTY_RAP: 'Faculty Representative',
  FACULTY_ADMIN: 'Faculty Admin',
  INSTITUTE_REPRESENTATIVE: 'Institute Representative',
  DEPARTMENT: 'Department Representative',
  APPLICANT: 'Applicant',
  ADMIN: 'Executive Director RID',
  REVIEWER: 'Research Ethics Committee Secretary',
};

const USER_TYPE_OPTIONS = [
  'ADMIN',
  'APPLICANT',
  'RESEARCH_CHAIR',
  'PROFESSOR',
  'REVIEWER',
  'DEPARTMENT',
  'FACULTY_RAP',
  'FACULTY_ADMIN',
  'INSTITUTE_REPRESENTATIVE',
  'RESEARCH_OFFICER',
  'ADMIN_CYBER',
];

const getUserTypeLabel = (userType?: string | null) => {
  if (!userType) return '—';
  return USER_TYPE_LABELS[userType] || userType;
};

const generateStrongPassword = (len = 16) => {
  const upp = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const low = 'abcdefghijkmnopqrstuvwxyz';
  const num = '23456789';
  const sym = '!@#$%^&*()-_=+[]{}:,.?';
  const all = upp + low + num + sym;
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  let pwd = pick(upp) + pick(low) + pick(num) + pick(sym);
  for (let i = pwd.length; i < len; i++) pwd += pick(all);
  return pwd.split('').sort(() => Math.random() - 0.5).join('');
};

const copyText = async (text: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
  return true;
};

/* =========================
   Modal (ESC close, scroll lock, a11y)
========================= */
const Modal: React.FC<ModalProps> = ({ visible, onClose, children, size = 'lg', titleId }) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('umx-lock');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('umx-lock');
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onClose]);

  if (!visible) return null;

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <ModalLayer
      className="umx-modalOverlay"
      onMouseDown={onOverlayClick}
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className={`umx-modal umx-modal--${size}`} onMouseDown={(e) => e.stopPropagation()}>
        <button className="umx-modalClose" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>
        {children}
      </div>
    </ModalLayer>
  );
};

/* =========================
   Component
========================= */
const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [toast, setToast] = useState<string | null>(null);
  const pushToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  };

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [registerModalVisible, setRegisterModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(true);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<{
    [key: string]: any;
    facultyId?: number | string;
    departmentId?: number | string;
  } | null>(null);

  const [newPassword, setNewPassword] = useState<string>('');
  const [includePassword, setIncludePassword] = useState<boolean>(false);

  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSavingRegister, setIsSavingRegister] = useState(false);

  const initialRegisterForm = {
    name: '',
    surname: '',
    title: '',
    specialization: '',
    gender: '',
    nationalId: '',
    dateOfBirth: '',
    nationality: '',
    highestLevelOfEducation: '',
    contactNumber: '',
    email: '',
    password: '',
    userType: 'APPLICANT',
    academicQualifications: '',
    registrationNo: '',
    facultyId: '',
    departmentId: '',
  };
  const [regForm, setRegForm] = useState<typeof initialRegisterForm>(initialRegisterForm);

  const [searchTerm, setSearchTerm] = useState('');
  const [facultyFilter, setFacultyFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  /* =========================
     Data fetchers
  ========================= */
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API}/v1/api/users/all`);
      const data = await res.json();
      if (data.success) setUsers(data.data);
      else setError(data.message || 'Error fetching users');
    } catch {
      setError('Error fetching users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFaculties = useCallback(async () => {
    try {
      const res = await fetch(`${API}/v1/api/systems-settings/faculties/get-all`);
      const data = await res.json();
      if (Array.isArray(data)) setFaculties(data);
      else if (data.success) setFaculties(data.data || []);
    } catch {}
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/v1/api/departments/all`);
      const data = await res.json();
      if (Array.isArray(data)) setDepartments(data);
      else if (data.success) setDepartments(data.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchFaculties();
    fetchDepartments();
  }, [fetchUsers, fetchFaculties, fetchDepartments]);

  /* =========================
     Helpers (view/edit)
  ========================= */
  const openViewUser = async (id: number) => {
    try {
      const res = await fetch(`${API}/v1/api/users/details/${id}`);
      const data = await res.json();
      if (data.success) {
        setSelectedUser(data.data);
        setViewDetailsOpen(true);
        setViewModalVisible(true);
      } else pushToast('Error fetching user details');
    } catch {
      pushToast('Error fetching user details');
    }
  };

  const openEditUser = async (id: number) => {
    try {
      const res = await fetch(`${API}/v1/api/users/details/${id}`);
      const data = await res.json();
      if (data.success) {
        const u = data.data as User;
        let facultyId: number | string | undefined = '';
        let departmentId: number | string | undefined = '';
        if (u.faculty && typeof u.faculty === 'object' && (u as any).faculty.id) {
          facultyId = (u as any).faculty.id;
        } else if (u.faculty && typeof u.faculty === 'string') {
          const f = faculties.find((x) => x.name === u.faculty);
          if (f) facultyId = f.id;
        }
        if (u.departmentId) departmentId = u.departmentId;
        else if (u.department) {
          const d = departments.find(
            (dep) => dep.name === u.department && dep.facultyId === Number(facultyId)
          );
          if (d) departmentId = d.id;
        }

        setEditForm({ ...u, facultyId, departmentId });
        setIncludePassword(false);
        setNewPassword('');
        setEditModalVisible(true);
      } else pushToast('Error fetching user details for edit');
    } catch {
      pushToast('Error fetching user details for edit');
    }
  };

  const handleDeleteUser = (id: number) => {
    setConfirmMessage('Are you sure you want to delete this user?');
    setConfirmAction(() => async () => {
      try {
        const res = await fetch(`${API}/v1/api/users/delete/${id}`, {
          method: 'DELETE',
        });
        const data = await res.json();
        if (data.success) {
          pushToast('User deleted');
          fetchUsers();
        } else pushToast('Error deleting user');
      } catch {
        pushToast('Error deleting user');
      }
      setConfirmModalVisible(false);
    });
    setConfirmModalVisible(true);
  };

  const handleToggleStatus = async (user: User) => {
    const newStatus = user.status === 'ACTIVE' ? 'DEACTIVE' : 'ACTIVE';
    try {
      const res = await fetch(
        `${API}/v1/api/users/update/${user.id}/status?status=${newStatus}`,
        { method: 'PUT' }
      );
      const data = await res.json();
      if (data.success) {
        pushToast(`Status: ${newStatus}`);
        fetchUsers();
      } else pushToast('Error updating status');
    } catch {
      pushToast('Error updating status');
    }
  };

  /* =========================
     Dependent selects & filters
  ========================= */
  const filteredDepartmentsReg = useMemo(
    () => (regForm.facultyId ? departments.filter((d) => d.facultyId === Number(regForm.facultyId)) : []),
    [regForm.facultyId, departments]
  );

  const filteredDepartmentsEdit = useMemo(
    () => (editForm?.facultyId ? departments.filter((d) => d.facultyId === Number(editForm.facultyId)) : []),
    [editForm?.facultyId, departments]
  );

  const filteredDepartmentsByFaculty = useMemo(
    () =>
      facultyFilter
        ? departments.filter((dep) => dep.facultyId === Number(facultyFilter))
        : departments,
    [facultyFilter, departments]
  );

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return users.filter((u) => {
      let matchFac = true;
      if (facultyFilter) {
        if (u.faculty && typeof u.faculty === 'object') {
          matchFac = String(u.faculty.id) === facultyFilter;
        } else if (u.faculty && typeof u.faculty === 'string') {
          const found = faculties.find((f) => f.name === u.faculty);
          matchFac = found ? String(found.id) === facultyFilter : false;
        } else matchFac = false;
      }
      let matchDept = true;
      if (departmentFilter) {
        if (u.departmentId) matchDept = String(u.departmentId) === departmentFilter;
        else if (u.department) {
          const found = departments.find(
            (dep) => dep.name === u.department && String(dep.id) === departmentFilter
          );
          matchDept = !!found;
        } else matchDept = false;
      }
      let matchSearch = true;
      if (q) {
        matchSearch = !!(
          (u.name && u.name.toLowerCase().includes(q)) ||
          (u.surname && u.surname.toLowerCase().includes(q)) ||
          (u.email && u.email.toLowerCase().includes(q)) ||
          (u.registrationNo && u.registrationNo.toLowerCase().includes(q)) ||
          (u.nationalId && u.nationalId.toLowerCase().includes(q))
        );
      }
      return matchFac && matchDept && matchSearch;
    });
  }, [users, faculties, departments, facultyFilter, departmentFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / perPage));
  const paginatedUsers = useMemo(
    () => filteredUsers.slice((page - 1) * perPage, page * perPage),
    [filteredUsers, page, perPage]
  );
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  /* =========================
     Forms
  ========================= */
  const handleEditFormChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!editForm) return;
    const { name, value } = e.target;
    if (name === 'facultyId') setEditForm({ ...editForm, facultyId: value, departmentId: '' });
    else if (name === 'departmentId') setEditForm({ ...editForm, departmentId: value });
    else setEditForm({ ...editForm, [name]: value });
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editForm || isSavingEdit) return;

    const payload: any = {
      name: editForm.name,
      surname: editForm.surname,
      title: editForm.title,
      specialization: editForm.specialization,
      gender: editForm.gender,
      nationalId: editForm.nationalId,
      dateOfBirth: editForm.dateOfBirth,
      nationality: editForm.nationality,
      highestLevelOfEducation: editForm.highestLevelOfEducation,
      contactNumber: editForm.contactNumber,
      email: editForm.email,
      academicQualifications: editForm.academicQualifications,
      registrationNo: editForm.registrationNo,
      departmentId: Number(editForm.departmentId),
      userType: editForm.userType,
    };
    if (includePassword && newPassword) payload.password = newPassword;

    try {
      setIsSavingEdit(true);
      const res = await fetch(`${API}/v1/api/users/edit/${editForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        pushToast('User updated');
        setEditModalVisible(false);
        setEditForm(null);
        fetchUsers();
      } else pushToast('Update failed');
    } catch {
      pushToast('Update failed');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleRegisterChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'facultyId') setRegForm((p) => ({ ...p, facultyId: value, departmentId: '' }));
    else if (name === 'departmentId') setRegForm((p) => ({ ...p, departmentId: value }));
    else setRegForm((p) => ({ ...p, [name]: value }));
  };

  const handleRegisterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSavingRegister) return;

    const payload: any = {
      name: regForm.name,
      surname: regForm.surname,
      title: regForm.title,
      specialization: regForm.specialization,
      gender: regForm.gender,
      nationalId: regForm.nationalId,
      dateOfBirth: regForm.dateOfBirth,
      nationality: regForm.nationality,
      highestLevelOfEducation: regForm.highestLevelOfEducation,
      contactNumber: regForm.contactNumber,
      email: regForm.email,
      password: regForm.password,
      academicQualifications: regForm.academicQualifications,
      registrationNo: regForm.registrationNo,
      departmentId: Number(regForm.departmentId),
      userType: regForm.userType,
    };

    try {
      setIsSavingRegister(true);
      const res = await fetch(`${API}/v1/api/users/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        pushToast('User created');
        setRegisterModalVisible(false);
        setRegForm(initialRegisterForm);
        fetchUsers();
      } else pushToast('Creation failed');
    } catch {
      pushToast('Creation failed');
    } finally {
      setIsSavingRegister(false);
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <div className="umx-wrap">
      <header className="umx-header">
        <div className="umx-header__text">
          <h1 className="umx-title">User Management</h1>
          <p className="umx-subtitle">Create, view, and manage all platform users.</p>
        </div>
        <div className="umx-header__actions">
          <button
            className="umx-btn umx-btn--pill umx-btn--ghost"
            onClick={() => setFiltersOpen((s) => !s)}
            aria-expanded={filtersOpen}
          >
            <FaFilter style={{ marginRight: 8 }} />
            Filters
          </button>
          <button className="umx-iconBtn" onClick={fetchUsers} title="Refresh">
            <FaSyncAlt />
          </button>
          <button className="umx-btn umx-btn--primary" onClick={() => setRegisterModalVisible(true)}>
            <FaUserPlus style={{ marginRight: 8 }} />
            Register New User
          </button>
        </div>
      </header>

      <StatCards items={[
        {label:'Users',value:users.length,total:users.length,meterLabel:'Accessible accounts'},
        {label:'Active accounts',value:users.filter(u=>u.status==='ACTIVE').length,total:users.length,meterLabel:'Of accessible accounts',tone:'success'},
        {label:'Matching filters',value:filteredUsers.length,total:users.length,meterLabel:'Current search results'}
      ]}/>
      <section className={`umx-filters ${filtersOpen ? 'is-open' : ''}`}>
        <input
          className="umx-input"
          type="search"
          placeholder="Search by name, email, reg no, or national ID"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          aria-label="Search users"
        />
        <select
          className="umx-select"
          value={facultyFilter}
          onChange={(e) => {
            setFacultyFilter(e.target.value);
            setDepartmentFilter('');
            setPage(1);
          }}
          aria-label="Filter by faculty"
        >
          <option value="">All Faculties</option>
          {faculties.map((fac) => (
            <option key={fac.id} value={fac.id}>
              {fac.name}
            </option>
          ))}
        </select>
        <select
          className="umx-select"
          value={departmentFilter}
          onChange={(e) => {
            setDepartmentFilter(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by department"
          disabled={!facultyFilter && departments.length > 0}
        >
          <option value="">All Departments</option>
          {filteredDepartmentsByFaculty.map((dep) => (
            <option key={dep.id} value={dep.id}>
              {dep.name}
            </option>
          ))}
        </select>

        <div className="umx-inline">
          <span className="umx-inline__label">Rows</span>
          <select
            className="umx-select umx-select--sm"
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Rows per page"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <button
          className="umx-btn umx-btn--warning umx-btn--pill"
          onClick={() => {
            setSearchTerm('');
            setFacultyFilter('');
            setDepartmentFilter('');
            setPage(1);
          }}
        >
          Clear
        </button>
      </section>

      {loading ? (
        <div className="umx-skeleton">
          <div className="umx-sk" />
          <div className="umx-sk" />
          <div className="umx-sk" />
        </div>
      ) : error ? (
        <p className="umx-error">{error}</p>
      ) : (
        <>
          <div className="umx-tableWrap">
            <table className="umx-table">
              <thead>
                <tr>
                  <th className="col-id">ID</th>
                  <th>Name</th>
                  <th className="col-email">Email</th>
                  <th className="col-type">User Type</th>
                  <th className="col-status">Status</th>
                  <th className="actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                      No users found.
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((u) => (
                    <tr key={u.id}>
                      <td className="col-id" data-label="ID">
                        {u.id}
                      </td>
                      <td data-label="Name">
                        <div className="umx-person"><UserAvatar userId={u.id} name={`${u.name} ${u.surname}`} size={36}/><span>{u.name} {u.surname}</span></div>
                      </td>
                      <td className="col-email" data-label="Email">
                        {u.email}
                      </td>
                      <td className="col-type" data-label="User Type">
                        {getUserTypeLabel(u.userType)}
                      </td>
                      <td className="col-status" data-label="Status">
                        <label className="umx-switch" aria-label={`Toggle status for ${u.name} ${u.surname}`}>
                          <input
                            type="checkbox"
                            checked={u.status === 'ACTIVE'}
                            onChange={() => handleToggleStatus(u)}
                          />
                          <span className="umx-slider" />
                        </label>
                      </td>
                      <td className="actions-col" data-label="Actions">
                        <div className="umx-row-actions">
                        <button onClick={() => openViewUser(u.id)} className="umx-btn umx-btn--tiny">
                          <FaEye aria-hidden="true"/> View
                        </button>
                        <button onClick={() => openEditUser(u.id)} className="umx-btn umx-btn--tiny">
                          <span aria-hidden="true">✎</span> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          className="umx-btn umx-btn--tiny umx-btn--danger"
                        >
                          <FaTimes aria-hidden="true"/> Delete
                        </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="umx-pagination">
            <button
              className="umx-btn umx-btn--ghost"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              aria-label="Previous page"
            >
              Prev
            </button>
            <span className="umx-pageIndex">
              Page {page} of {totalPages}
            </span>
            <button
              className="umx-btn umx-btn--ghost"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              Next
            </button>
            <span className="umx-results">
              {filteredUsers.length} result{filteredUsers.length === 1 ? '' : 's'}
            </span>
          </div>
        </>
      )}

      <Modal
        visible={viewModalVisible}
        onClose={() => setViewModalVisible(false)}
        size="lg"
        titleId="umx-view-title"
      >
        {selectedUser && (
          <div className="umx-modalBody">
            <h3 id="umx-view-title" className="umx-modalTitle">
              User Details
            </h3>

            <div className="umx-summary">
              <div className="umx-chip">
                <span className="umx-chip__label">Name</span>
                <span className="umx-chip__value">
                  {selectedUser.title ? `${selectedUser.title} ` : ''}
                  {selectedUser.name} {selectedUser.surname}
                </span>
              </div>
              <div className="umx-chip">
                <span className="umx-chip__label">Email</span>
                <span className="umx-chip__value">{selectedUser.email}</span>
              </div>
              <div className="umx-chip">
                <span className="umx-chip__label">Type</span>
                <span className="umx-chip__value">{getUserTypeLabel(selectedUser.userType)}</span>
              </div>
              <div className="umx-chip">
                <span className="umx-chip__label">Status</span>
                <span className={`umx-badge ${selectedUser.status === 'ACTIVE' ? 'is-ok' : 'is-off'}`}>
                  {selectedUser.status || 'N/A'}
                </span>
              </div>
            </div>

            <button
              className="umx-sectionToggle"
              onClick={() => setViewDetailsOpen((o) => !o)}
              aria-expanded={viewDetailsOpen}
            >
              <span>{viewDetailsOpen ? 'Hide full details' : 'Show full details'}</span>
              {viewDetailsOpen ? <FaChevronDown /> : <FaChevronRight />}
            </button>

            {viewDetailsOpen && (
              <div className="umx-defGrid">
                <div className="umx-defCol">
                  <div className="umx-def"><span>Specialization</span><strong>{selectedUser.specialization}</strong></div>
                  <div className="umx-def"><span>Gender</span><strong>{selectedUser.gender}</strong></div>
                  <div className="umx-def"><span>National ID</span><strong>{selectedUser.nationalId}</strong></div>
                  <div className="umx-def"><span>D.O.B</span><strong>{selectedUser.dateOfBirth}</strong></div>
                  <div className="umx-def"><span>Nationality</span><strong>{selectedUser.nationality}</strong></div>
                </div>
                <div className="umx-defCol">
                  <div className="umx-def"><span>Highest Education</span><strong>{selectedUser.highestLevelOfEducation}</strong></div>
                  <div className="umx-def"><span>Contact</span><strong>{selectedUser.contactNumber}</strong></div>
                  <div className="umx-def"><span>Registration No</span><strong>{selectedUser.registrationNo}</strong></div>
                  <div className="umx-def"><span>Qualifications</span><strong>{selectedUser.academicQualifications || '—'}</strong></div>
                  <div className="umx-def"><span>User Type</span><strong>{getUserTypeLabel(selectedUser.userType)}</strong></div>
                </div>
                <div className="umx-defCol">
                  <div className="umx-def">
                    <span>Faculty</span>
                    <strong>
                      {selectedUser.faculty
                        ? typeof selectedUser.faculty === 'object'
                          ? selectedUser.faculty.name
                          : selectedUser.faculty
                        : '—'}
                    </strong>
                  </div>
                  <div className="umx-def">
                    <span>Department</span>
                    <strong>
                      {(() => {
                        if ((selectedUser as any).departmentId) {
                          const dep = departments.find((d) => d.id === (selectedUser as any).departmentId);
                          return dep ? dep.name : selectedUser.department || '—';
                        }
                        return selectedUser.department || '—';
                      })()}
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        size="xl"
        titleId="umx-edit-title"
      >
        {editForm && (
          <form className="umx-modalBody" onSubmit={handleSaveEdit}>
            <h3 id="umx-edit-title" className="umx-modalTitle">Edit User</h3>

            <div className="umx-formGrid">
              <input aria-label="Name" className="umx-input" type="text" name="name" value={editForm.name} onChange={handleEditFormChange} required placeholder="Name" />
              <input aria-label="Surname" className="umx-input" type="text" name="surname" value={editForm.surname} onChange={handleEditFormChange} required placeholder="Surname" />
              <input aria-label="Title" className="umx-input" type="text" name="title" value={editForm.title} onChange={handleEditFormChange} placeholder="Title" />
              <input aria-label="Specialization" className="umx-input" type="text" name="specialization" value={editForm.specialization} onChange={handleEditFormChange} placeholder="Specialization" />
              <select aria-label="gender" className="umx-select" name="gender" value={editForm.gender} onChange={handleEditFormChange}>
                <option value="">Gender</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
              <input aria-label="National ID" className="umx-input" type="text" name="nationalId" value={editForm.nationalId} onChange={handleEditFormChange} placeholder="National ID" />
              <input aria-label="date Of Birth" className="umx-input" type="date" name="dateOfBirth" value={editForm.dateOfBirth} onChange={handleEditFormChange} />
              <input aria-label="Nationality" className="umx-input" type="text" name="nationality" value={editForm.nationality} onChange={handleEditFormChange} placeholder="Nationality" />
              <input aria-label="Highest Education" className="umx-input" type="text" name="highestLevelOfEducation" value={editForm.highestLevelOfEducation} onChange={handleEditFormChange} placeholder="Highest Education" />
              <input aria-label="Contact Number" className="umx-input" type="text" name="contactNumber" value={editForm.contactNumber} onChange={handleEditFormChange} placeholder="Contact Number" />
              <input aria-label="Email" className="umx-input" type="email" name="email" value={editForm.email} onChange={handleEditFormChange} placeholder="Email" />

              <select aria-label="user Type" className="umx-select" name="userType" value={editForm.userType} onChange={handleEditFormChange} required>
                <option value="">User Type</option>
                {USER_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>
                    {getUserTypeLabel(type)}
                  </option>
                ))}
              </select>

              <select aria-label="faculty Id" className="umx-select" name="facultyId" value={editForm.facultyId ? String(editForm.facultyId) : ''} onChange={handleEditFormChange}>
                <option value="">Faculty</option>
                {faculties.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>

              <select aria-label="department Id"
                className="umx-select"
                name="departmentId"
                value={editForm.departmentId ? String(editForm.departmentId) : ''}
                onChange={handleEditFormChange}
                disabled={!editForm.facultyId}
              >
                <option value="">Department</option>
                {filteredDepartmentsEdit.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>

              <input aria-label="Academic Qualifications" className="umx-input" type="text" name="academicQualifications" value={editForm.academicQualifications || ''} onChange={handleEditFormChange} placeholder="Academic Qualifications" />
              <input aria-label="Registration No" className="umx-input" type="text" name="registrationNo" value={editForm.registrationNo || ''} onChange={handleEditFormChange} placeholder="Registration No" />
            </div>

            <section className="umx-reset">
              <div className="umx-reset__row">
                <label className="umx-reset__label">
                  <FaKey style={{ marginRight: 8 }} /> Password Reset
                </label>
                <div className="umx-reset__actions">
                  <button
                    type="button"
                    className="umx-btn umx-btn--soft"
                    onClick={() => {
                      const p = generateStrongPassword(18);
                      setNewPassword(p);
                      setIncludePassword(true);
                      pushToast('New password generated');
                    }}
                  >
                    <FaSyncAlt style={{ marginRight: 6 }} />
                    Generate strong password
                  </button>
                  <button
                    type="button"
                    className="umx-btn umx-btn--soft"
                    disabled={!newPassword}
                    onClick={async () => {
                      if (!newPassword) return;
                      await copyText(newPassword);
                      pushToast('Password copied to clipboard');
                    }}
                  >
                    <FaCopy style={{ marginRight: 6 }} />
                    Copy
                  </button>
                </div>
              </div>

              <div className="umx-reset__preview">
                <input aria-label="No password generated (optional)" className="umx-input umx-input--mono" type="text" value={newPassword} readOnly placeholder="No password generated (optional)" />
                <label className="umx-checkbox">
                  <input
                    type="checkbox"
                    checked={includePassword}
                    onChange={(e) => setIncludePassword(e.target.checked)}
                    disabled={!newPassword}
                  />
                  <span>Include this password in update</span>
                </label>
              </div>

              <p className="umx-reset__hint">
                You can edit user details and save without changing their password. Only when you generate & include a new password will it be updated.
              </p>
            </section>

            <div className="umx-modalActions">
              <button type="submit" className="umx-btn umx-btn--primary" disabled={isSavingEdit}>
                {isSavingEdit ? 'Saving…' : 'Save Changes'}
              </button>
              <button type="button" className="umx-btn umx-btn--ghost" onClick={() => setEditModalVisible(false)} disabled={isSavingEdit}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        visible={registerModalVisible}
        onClose={() => setRegisterModalVisible(false)}
        size="xl"
        titleId="umx-register-title"
      >
        <form className="umx-modalBody" onSubmit={handleRegisterSubmit}>
          <h3 id="umx-register-title" className="umx-modalTitle">Register New User</h3>

          <div className="umx-formGrid">
            <input aria-label="Name" className="umx-input" type="text" name="name" value={regForm.name} onChange={handleRegisterChange} required placeholder="Name" />
            <input aria-label="Surname" className="umx-input" type="text" name="surname" value={regForm.surname} onChange={handleRegisterChange} required placeholder="Surname" />
            <input aria-label="Title" className="umx-input" type="text" name="title" value={regForm.title} onChange={handleRegisterChange} placeholder="Title" />
            <input aria-label="Specialization" className="umx-input" type="text" name="specialization" value={regForm.specialization} onChange={handleRegisterChange} placeholder="Specialization" />

            <select aria-label="gender" className="umx-select" name="gender" value={regForm.gender} onChange={handleRegisterChange}>
              <option value="">Gender</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
            <input aria-label="National ID" className="umx-input" type="text" name="nationalId" value={regForm.nationalId} onChange={handleRegisterChange} placeholder="National ID" />
            <input aria-label="date Of Birth" className="umx-input" type="date" name="dateOfBirth" value={regForm.dateOfBirth} onChange={handleRegisterChange} />
            <input aria-label="Nationality" className="umx-input" type="text" name="nationality" value={regForm.nationality} onChange={handleRegisterChange} placeholder="Nationality" />
            <input aria-label="Highest Education" className="umx-input" type="text" name="highestLevelOfEducation" value={regForm.highestLevelOfEducation} onChange={handleRegisterChange} placeholder="Highest Education" />
            <input aria-label="Contact Number" className="umx-input" type="text" name="contactNumber" value={regForm.contactNumber} onChange={handleRegisterChange} placeholder="Contact Number" />

            <input aria-label="Email" className="umx-input" type="email" name="email" value={regForm.email} onChange={handleRegisterChange} required placeholder="Email" />

            <div className="umx-genRow">
              <input aria-label="Generated password (or type your own)"
                className="umx-input umx-input--mono"
                type="text"
                name="password"
                value={regForm.password}
                onChange={handleRegisterChange}
                placeholder="Generated password (or type your own)"
                required
              />
              <button
                type="button"
                className="umx-btn umx-btn--soft"
                onClick={() => {
                  const p = generateStrongPassword(18);
                  setRegForm((prev) => ({ ...prev, password: p }));
                  pushToast('Password generated');
                }}
                title="Generate strong password"
              >
                <FaKey style={{ marginRight: 6 }} />
                Generate
              </button>
              <button
                type="button"
                className="umx-btn umx-btn--soft"
                disabled={!regForm.password}
                onClick={async () => {
                  await copyText(regForm.password);
                  pushToast('Password copied to clipboard');
                }}
                title="Copy password"
              >
                <FaCopy />
              </button>
            </div>

            <select aria-label="user Type" className="umx-select" name="userType" value={regForm.userType} onChange={handleRegisterChange} required>
              <option value="">User Type</option>
              {USER_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {getUserTypeLabel(type)}
                </option>
              ))}
            </select>

            <select aria-label="faculty Id" className="umx-select" name="facultyId" value={regForm.facultyId} onChange={handleRegisterChange} required>
              <option value="">Faculty</option>
              {faculties.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>

            <select aria-label="department Id"
              className="umx-select"
              name="departmentId"
              value={regForm.departmentId}
              onChange={handleRegisterChange}
              required
              disabled={!regForm.facultyId}
            >
              <option value="">Department</option>
              {filteredDepartmentsReg.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>

            <input aria-label="Academic Qualifications" className="umx-input" type="text" name="academicQualifications" value={regForm.academicQualifications} onChange={handleRegisterChange} placeholder="Academic Qualifications" />
            <input aria-label="Registration No" className="umx-input" type="text" name="registrationNo" value={regForm.registrationNo} onChange={handleRegisterChange} placeholder="Registration No" />
          </div>

          <div className="umx-modalActions">
            <button type="submit" className="umx-btn umx-btn--primary" disabled={isSavingRegister}>
              {isSavingRegister ? 'Registering…' : 'Register'}
            </button>
            <button type="button" className="umx-btn umx-btn--ghost" onClick={() => setRegisterModalVisible(false)} disabled={isSavingRegister}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      <Modal visible={confirmModalVisible} onClose={() => setConfirmModalVisible(false)} size="md" titleId="umx-confirm-title">
        <div className="umx-modalBody">
          <h3 id="umx-confirm-title" className="umx-modalTitle">Confirm</h3>
          <p className="umx-confirmText">{confirmMessage}</p>
          <div className="umx-modalActions umx-modalActions--center">
            <button className="umx-btn umx-btn--primary" onClick={() => confirmAction()}>
              Yes
            </button>
            <button className="umx-btn umx-btn--danger" onClick={() => setConfirmModalVisible(false)}>
              No
            </button>
          </div>
        </div>
      </Modal>

      {toast && <div className="umx-toast">{toast}</div>}
    </div>
  );
};

export default UserManagement;
